import { Check, Unlock } from 'lucide-react';
import Slide from '@/components/shared/Slide';
import type { EligibilityChecklist, ViewMode } from '@/types/roadmap.types';

interface Props {
  checklist: EligibilityChecklist;
  mode: ViewMode;
}

function CheckItem({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-border/50 p-4 print:p-3 print:rounded-lg transition-colors hover:border-emerald-200 hover:bg-emerald-50/30">
      <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
        <Check className="w-3.5 h-3.5 text-emerald-600" />
      </div>
      <span className="text-sm print:text-[9pt] text-foreground">{label}</span>
    </div>
  );
}

export default function RequirementsSlide({ checklist, mode }: Props) {
  const hasGroups = checklist.groups && checklist.groups.length > 0;

  return (
    <Slide mode={mode}>
      <div className="w-full max-w-4xl mx-auto">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">
          Prerequisites
        </p>
        <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-2 print:text-2xl print:mb-1">
          Basic Requirements
        </h2>
        <p className="text-sm text-muted-foreground mb-8 print:mb-5">
          Required registrations for{' '}
          <strong className="text-foreground">{checklist.jurisdiction}</strong>
        </p>

        {hasGroups ? (
          <div className="space-y-6 print:space-y-4">
            {checklist.groups!.map((group) => (
              <div key={group.label}>
                <h3 className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground mb-3 print:mb-2 print:text-[8pt]">
                  {group.label}
                </h3>
                <div className="grid sm:grid-cols-2 gap-3 print:gap-2">
                  {group.items.map((item, i) => (
                    <CheckItem key={i} label={item} />
                  ))}
                </div>
                {group.unlock_note && (
                  <div className="mt-3 flex items-start gap-2.5 rounded-lg border border-violet-200/70 bg-violet-50/40 px-4 py-3 print:mt-2 print:px-3 print:py-2">
                    <Unlock className="w-4 h-4 text-violet-500 shrink-0 mt-0.5" />
                    <p className="text-sm print:text-[8pt] text-violet-900/80 leading-relaxed print:leading-snug">
                      {group.unlock_note}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-3">
            {checklist.items.map((item, i) => (
              <CheckItem key={i} label={item} />
            ))}
          </div>
        )}
      </div>
    </Slide>
  );
}
